/**
 * ModeSelectionScreen — entry screen with the two big "Live" / "Video" cards.
 *
 * Mirrors renderModeSelection() from EstadisticasNew.js, restyled with the
 * v2 theme. The "go" handler is delegated to the parent (StatsRouter) which
 * decides whether to open a config dialog or jump straight in.
 */

import React from 'react';
import { Box, Typography, ButtonBase } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';
import { STATS_MODE } from '../constants';

function ModeCard({ icon, title, description, accent, onClick }) {
  const theme = useStatsTheme();
  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        flex: 1,
        minHeight: 280,
        p: 4,
        borderRadius: theme.radius.xl,
        backgroundColor: theme.surfaces.elevated,
        border: `1px solid ${theme.surfaces.border}`,
        boxShadow: theme.shadows.md,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        transition: `transform ${theme.motion.base}, border-color ${theme.motion.base}, box-shadow ${theme.motion.base}`,
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: accent,
          boxShadow: `0 24px 48px ${withAlpha(accent, 0.32)}`,
          backgroundColor: theme.surfaces.floating,
        },
      }}
    >
      <Box
        sx={{
          width: 88,
          height: 88,
          borderRadius: '50%',
          backgroundColor: withAlpha(accent, 0.16),
          border: `2px solid ${withAlpha(accent, 0.4)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: 22,
          fontWeight: 800,
          color: theme.text.primary,
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          color: theme.text.secondary,
          textAlign: 'center',
          maxWidth: 260,
          lineHeight: 1.5,
        }}
      >
        {description}
      </Typography>
    </ButtonBase>
  );
}

export default function ModeSelectionScreen({ onSelectMode, t }) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  return (
    <Box
      sx={{
        backgroundColor: theme.surfaces.base,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
        gap: 5,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 720 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            color: theme.accent,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            mb: 1.5,
          }}
        >
          {tt('newStatsSession', 'New stats session')}
        </Typography>
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: { xs: 28, md: 36 },
            fontWeight: 800,
            color: theme.text.primary,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {tt('selectStatsMode', 'How do you want to scout this game?')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          width: '100%',
          maxWidth: 880,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        <ModeCard
          icon={<RadioButtonCheckedIcon sx={{ fontSize: 44 }} />}
          title={tt('liveStats', 'Live stats')}
          description={tt('liveDescription', 'Record actions in real time during the game. Track minutes, fouls, and score as it happens.')}
          accent={theme.actionColors.missed}
          onClick={() => onSelectMode(STATS_MODE.LIVE)}
        />
        <ModeCard
          icon={<VideocamIcon sx={{ fontSize: 44 }} />}
          title={tt('videoStats', 'Video stats')}
          description={tt('videoDescription', 'Tag actions on a recorded game. Scrub the video, place shots on the court, and review.')}
          accent={theme.accent}
          onClick={() => onSelectMode(STATS_MODE.VIDEO)}
        />
      </Box>
    </Box>
  );
}
